/**
 * GoalsTab
 *
 * The firm's annual billable-hours requirement per title — one row per title,
 * not per person. A firm sets "partners bill 1,700 a year"; it does not set a
 * separate number for each partner.
 *
 * Only fee-earning titles appear. Staff titles (IT, HR, Administration…) never
 * carry a billable requirement, so listing them would be rows of zeroes.
 *
 * Monthly and Weekly measure each timekeeper against the requirement for their
 * title, resolved from their raw prof_type via profTitles.ts.
 *
 * There is no backend goal store yet, so these persist to this browser only.
 */

import { useState } from "react";
import { StepInput } from "@/features/attorney/components/SettingsPanel/SettingsShared";
import type { GoalsByGroup } from "../../types/reports";
import {
	BILLING_TITLE_GROUPS,
	type ProfTitleGroup,
} from "../../utils/profTitles";
import ReportTable, { type ReportColumn } from "./ReportTable";

/** Arrow clicks move in useful increments; 1 hour a year is not one. */
const STEP_HOURS = 25;

interface GoalsTabProps {
	goals: GoalsByGroup;
	/** Only the titles the firm has overridden, for the "default" marker. */
	stored: GoalsByGroup;
	onUpdate: (group: ProfTitleGroup, annualHours: number) => void;
	onResetAll: () => void;
}

/**
 * Annual-hours field, using the same StepInput as Settings → Tracking.
 *
 * Keeps a string draft so a half-typed number is not parsed on every keystroke,
 * and commits on blur, Enter or an arrow press.
 */
function GoalStepInput({
	group,
	value,
	onUpdate,
}: {
	group: ProfTitleGroup;
	value: number;
	onUpdate: (group: ProfTitleGroup, annualHours: number) => void;
}) {
	const [draft, setDraft] = useState(String(value));

	function commit(next?: number) {
		const parsed = next ?? Number(draft);
		if (!Number.isFinite(parsed) || parsed < 0) {
			setDraft(String(value));
			return;
		}
		const rounded = Math.round(parsed);
		setDraft(String(rounded));
		if (rounded !== value) onUpdate(group, rounded);
	}

	return (
		<StepInput
			value={draft}
			onChange={setDraft}
			onBlur={() => commit()}
			onStep={(delta) => {
				const base = Number.isFinite(Number(draft)) ? Number(draft) : value;
				commit(Math.max(0, base + delta * STEP_HOURS));
			}}
		/>
	);
}

export default function GoalsTab({
	goals,
	stored,
	onUpdate,
	onResetAll,
}: GoalsTabProps) {
	const columns: Array<ReportColumn<ProfTitleGroup>> = [
		{ key: "group", header: "Title", cell: (g) => g },
		{
			key: "annual",
			header: "Annual hours",
			align: "right",
			width: "12rem",
			cell: (g) => (
				<div className="flex items-center justify-end gap-2">
					{stored[g] === undefined && (
						<span
							className="text-xs text-muted-foreground"
							title="Hourglass default — not yet set by the firm"
						>
							default
						</span>
					)}
					<GoalStepInput group={g} value={goals[g] ?? 0} onUpdate={onUpdate} />
				</div>
			),
		},
		{
			key: "monthly",
			header: "Monthly",
			align: "right",
			width: "7rem",
			cell: (g) => ((goals[g] ?? 0) / 12).toFixed(1),
		},
		{
			key: "weekly",
			header: "Weekly",
			align: "right",
			width: "7rem",
			cell: (g) => ((goals[g] ?? 0) / 52).toFixed(1),
		},
	];

	return (
		<div className="flex flex-col gap-6">
			<div className="flex flex-wrap items-center justify-between gap-3">
				<div>
					<h2 className="text-lg font-medium text-foreground">Requirements</h2>
					<p className="text-sm text-muted-foreground">
						Annual billable-hours goal per title. Every timekeeper is measured
						against the requirement for their title.
					</p>
				</div>
				<button
					type="button"
					onClick={onResetAll}
					className="rounded-md border border-border px-3 py-1.5 text-sm text-muted-foreground hover:bg-muted"
				>
					Reset to defaults
				</button>
			</div>

			<ReportTable
				columns={columns}
				rows={BILLING_TITLE_GROUPS}
				rowKey={(g) => g}
			/>

			<p className="text-xs text-muted-foreground">
				Goals are stored in this browser only and are not yet shared across
				users or devices; backend persistence is a follow-up.
			</p>
		</div>
	);
}
